import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import Auth from './Auth';
import App from './App';
import { LogOut, User } from 'lucide-react';

const AppWithAuth = () => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  // Session timeout: 30 minutes of inactivity
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
  const WARNING_TIME = 2 * 60 * 1000; // Show warning 2 minutes before timeout
  let inactivityTimer = null;
  let warningTimer = null;

  // Reset inactivity timer on user activity
  const resetInactivityTimer = () => {
    // Clear existing timers
    if (inactivityTimer) clearTimeout(inactivityTimer);
    if (warningTimer) clearTimeout(warningTimer);
    setShowTimeoutWarning(false);

    // Set warning timer (2 minutes before logout)
    warningTimer = setTimeout(() => {
      setShowTimeoutWarning(true);
    }, INACTIVITY_TIMEOUT - WARNING_TIME);

    // Set logout timer (30 minutes)
    inactivityTimer = setTimeout(() => {
      handleAutoLogout();
    }, INACTIVITY_TIMEOUT);
  };

  const handleAutoLogout = async () => {
    alert('You have been logged out due to inactivity.');
    await supabase.auth.signOut();
    setSession(null);
    setShowTimeoutWarning(false);
  };

  const handleManualLogout = async () => {
    try {
      // Sign out from Supabase FIRST
      await supabase.auth.signOut();
      
      // Clear all local storage related to Supabase
      Object.keys(localStorage).forEach(key => {
        if (key.includes('supabase') || key.includes('sb-')) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear state
      setSession(null);
      setUserEmail('');
      
      // Clear timers
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (warningTimer) clearTimeout(warningTimer);
      
      // Small delay to ensure logout completes, then reload
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    } catch (err) {
      console.error('Logout error:', err);
      // Force clear everything even on error
      localStorage.clear();
      window.location.href = '/';
    }
  };

  useEffect(() => {
    // Check active sessions
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUserEmail(session?.user?.email || '');
      setLoading(false);
      
      // Start inactivity timer if logged in
      if (session) {
        resetInactivityTimer();
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUserEmail(session?.user?.email || '');
      
      // Reset timer on auth change
      if (session) {
        resetInactivityTimer();
      }
    });

    // Add activity listeners
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, resetInactivityTimer);
    });

    return () => {
      subscription.unsubscribe();
      // Cleanup timers
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (warningTimer) clearTimeout(warningTimer);
      // Remove activity listeners
      events.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return <Auth />;
  }

  return (
    <div className="relative">
      {/* Session Timeout Warning */}
      {showTimeoutWarning && (
        <div className="fixed top-20 right-4 z-50 bg-yellow-100 border-2 border-yellow-500 rounded-lg p-4 shadow-lg max-w-sm">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="font-bold text-yellow-800 mb-1">Session Expiring Soon!</h3>
              <p className="text-sm text-yellow-700">You will be logged out in 2 minutes due to inactivity. Move your mouse to stay logged in.</p>
            </div>
            <button
              onClick={() => {
                setShowTimeoutWarning(false);
                resetInactivityTimer();
              }}
              className="text-yellow-600 hover:text-yellow-800"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* User Info Bar */}
      <div className="fixed top-0 right-0 z-50 m-4">
        <div className="bg-white shadow-lg rounded-lg px-4 py-2 flex items-center gap-3 border">
          <User size={18} className="text-blue-600" />
          <span className="text-sm font-medium text-gray-700">{userEmail}</span>
          <button
            onClick={handleManualLogout}
            className="ml-2 text-red-600 hover:text-red-800 transition-colors p-1 rounded hover:bg-red-50"
            title="Sign Out"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* Main App */}
      <App userId={session.user.id} userEmail={userEmail} />
    </div>
  );
};

export default AppWithAuth;